import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import {
  ChangeUserPasswordRequest,
  CreateEmloyeesImportRequest,
  EmployeeAppSettings,
  EmployeeBenefitBudget,
  EmployeeBulkEditDataRequest,
  EmployeeExtended,
  EmployeeExtendedRequest,
  EmployeeGeneralDataRequest,
  EmployeeTaxDataRequest,
  EmployeePositionDataRequest,
  OperationEnum,
  PatchedEmployeeAddressRequest,
  PatchedEmployeeAppSettingsRequest,
  PatchedEmployeeContactDataRequest,
  PatchedEmployeeExtendedRequest,
  PatchedEmployeeGeneralDataRequest,
  PatchedEmployeePositionDataRequest,
  PatchedEmployeeTaxDataRequest,
  UserRequest,
  EmployeeGroupUpdateRequest,
  MonthField,
  EmployeeListItem,
  PatchedEmployeeBulkGroupRequest
} from '@pxtra-client-lib-legacy/gen';
import { format, parseISO } from 'date-fns';
import { makeAutoObservable, runInAction, when } from 'mobx';
import Util from '../../misc/util';
import { Writeable } from '../../misc/writeable';
import AuthenticationStore from '../authenticationStore';
import CompanyStore from '../companyStore';

type CreateEmployeeProps = {
  first_name: EmployeeGeneralDataRequest['first_name'];
  last_name: EmployeeGeneralDataRequest['last_name'];
  gender: EmployeeGeneralDataRequest['gender'];
  birth_date: EmployeeGeneralDataRequest['birth_date'];
  marriage_status: EmployeeGeneralDataRequest['marriage_status'];
  children_count: EmployeeTaxDataRequest['children_count'];
  email: UserRequest['email'];
  personal_number: EmployeePositionDataRequest['employee_number'];
};

export interface ImportKomboEmployeeProps {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  nationality: string;
  gender: string;
  marital_status: string;
  home_address: {
    city: string;
    country: string;
    state: string;
    street_1: string;
    zip_code: string;
  };
  mobile_phone_number: string;
  personal_email: string;
  job_title: string;
  employee_number: string;
  start_date: string;
  termination_date: string;
  work_email: string;
  work_location: {
    address: {
      city: string;
    };
  };
}

export interface EmployeeMapped {
  first_name: string | undefined;
  last_name: string | undefined;
  birth_date: string | undefined;
  nationality: string | undefined;
  gender: string | undefined;
  marriage_status: string | undefined;
  street_name_and_number: string | undefined;
  zip_code: string | undefined;
  city: string | undefined;
  province: string | undefined;
  country: string | undefined;
  mobile_phone: string | undefined;
  email: string | undefined;
  location: string | undefined;
  position_name: string | undefined;
  employee_number: string | undefined;
  entry_date: string | undefined;
  exit_date: string | undefined;
  user: string | undefined;
  hris_id: string | undefined;
}

class EmployeeStore {
  private apiClient: PxtraApiClientLegacy;

  private _currentEmployee?: EmployeeExtended;
  private _appSettings?: EmployeeAppSettings;

  constructor(
    apiClient: PxtraApiClientLegacy,
    private companyStore: CompanyStore,
    private authenticationStore: AuthenticationStore
  ) {
    this.apiClient = apiClient;
    makeAutoObservable(this);
    when(
      () => !this.authenticationStore.isLoggedIn,
      () => {
        this._clear();
      }
    );
  }

  private _clear() {
    this._currentEmployee = undefined;
    this._appSettings = undefined;
  }

  public get appSettings() {
    return this._appSettings;
  }

  public get currentEmployee() {
    return this._currentEmployee;
  }

  public get receiptBenefits() {
    if (this._currentEmployee) {
      return Util.getReceiptsBenefits(this._currentEmployee);
    }
    return [];
  }

  public async importEmployees({
    file,
    column_mapping
  }: Omit<CreateEmloyeesImportRequest, 'file' | 'import_type' | 'company'> & {
    file: { encoded_string: string; name: string };
  }) {
    if (!this.companyStore.company) throw Error('No company selected');
    return this.apiClient.impex.impexImportCreate({
      requestBody: {
        company: this.companyStore.company.id,
        import_type: 'employees',
        file: file,
        column_mapping
      }
    });
  }

  public async createNewEmployee(data: CreateEmployeeProps) {
    const defaults: EmployeeExtendedRequest = {
      general_data: {
        first_name: !data.first_name ? undefined : data.first_name,
        last_name: !data.last_name ? undefined : data.last_name,
        gender: !data.gender ? undefined : data.gender,
        marriage_status: !data.marriage_status ? undefined : data.marriage_status,
        birth_date: !data.birth_date ? undefined : data.birth_date
      },
      address: {},
      contact_data: {
        email: data.email
      },
      tax_data: {
        children_count: !data.children_count ? undefined : data.children_count
      },
      position_data: {
        employee_number: !data.personal_number ? undefined : data.personal_number
      },
      user: { email: data.email },
      company: this.companyStore.company?.id,
      department: null,
      location: null
    };

    const employee = await this.apiClient.employees.employeesCreate({ requestBody: defaults });
    return employee.id;
  }

  public async getGeneralData(id: string) {
    return await this.apiClient.employees.employeesGeneralDataRetrieve({
      id
    });
  }

  public async fetchOne(id: string) {
    const employee = await this.apiClient.employees.employeesRetrieve({ id: id });

    return runInAction(() => (this._currentEmployee = employee));
  }

  public async fetchEmployeeBudgetInformation(employeeId: string, date: string) {
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();

    return this.fetchEmployeeBudgetInformationByMonth(employeeId, { month, year });
  }

  public async fetchEmployeeBudgetInformationByMonth(employeeId: string, month: MonthField) {
    const budget = await this.apiClient.employees.employeesBenefitBudgetRetrieve({
      id: employeeId,
      accountingPeriodMonth: month.month,
      accountingPeriodYear: month.year
    });

    runInAction(() => {
      if (this._currentEmployee) {
        (this._currentEmployee.benefit_budget as Writeable<EmployeeBenefitBudget>) = budget;
      }
    });
    return budget;
  }

  public async fetchBudgetUpdates(employeeId: string) {
    if (!employeeId) {
      return;
    }
    const result = await this.apiClient.employees.employeesBenefitBudgetUpdatesList({
      accountingPeriodMonth: new Date().getMonth() + 1,
      accountingPeriodYear: new Date().getFullYear(),
      id: employeeId
    });
    return result.results;
  }

  public async fetchList(
    selectFields: (keyof EmployeeListItem)[] = [
      'available_benefit_budget',
      'benefits',
      'birth_date',
      'cost_center',
      'department',
      'employee_number',
      'entry_date',
      'exit_date',
      'first_name',
      'has_missing_fields',
      'id',
      'last_name',
      'location',
      'member_groups',
      'note',
      'position_name',
      'profile_picture'
    ]
  ) {
    if (!this.companyStore.company) throw Error('No company selected');

    const result = await this.apiClient.employees.employeesList({
      company: this.companyStore.company.id,
      offset: 0,
      limit: 500,
      selectFields
    });

    return result.results;
  }

  public async mapKomboEmployees(employees: ImportKomboEmployeeProps[]) {
    const mappedEmployees = [];
    for (const employee of employees) {
      const data: EmployeeMapped = {
        first_name: employee.first_name,
        last_name: employee.last_name,
        birth_date: !employee.date_of_birth
          ? undefined
          : format(parseISO(employee.date_of_birth), 'yyyy-MM-dd'),
        nationality: employee.nationality,
        gender: employee.gender ? employee.gender.toLowerCase() : undefined,
        marriage_status: employee.marital_status
          ? employee.marital_status.toLowerCase()
          : undefined,
        street_name_and_number: employee.home_address.street_1,
        zip_code: employee.home_address.zip_code,
        city: employee.home_address.city,
        province: employee.home_address.state,
        country: employee.home_address.country,
        mobile_phone: employee.mobile_phone_number,
        email: employee.personal_email,
        location: employee.work_location?.address?.city,
        position_name: employee.job_title,
        employee_number: employee.employee_number,
        entry_date: !employee.start_date
          ? undefined
          : format(parseISO(employee.start_date), 'yyyy-MM-dd'),
        exit_date: !employee.termination_date
          ? undefined
          : format(parseISO(employee.termination_date), 'yyyy-MM-dd'),
        user: employee.work_email,
        hris_id: employee.id
      };
      mappedEmployees.push(data);
    }
    return mappedEmployees;
  }

  public async getEmployeesEmailList(company: string) {
    const existingEmployees = await this.apiClient.employees.employeesList({
      company: company
    });
    const existingIds = existingEmployees.results?.map(({ id }) => id);
    const newList: { id: string; email: string | undefined }[] = [];
    if (existingIds) {
      for (const id of existingIds) {
        if (id) {
          const employee = await this.apiClient.employees.employeesRetrieve({
            id: id
          });
          newList.push({ id: id, email: employee.user?.email });
        }
      }
    }
    return newList;
  }

  public async updateAppSettings(id: string, data: PatchedEmployeeAppSettingsRequest) {
    await this.apiClient.employees
      .employeesAppSettingsPartialUpdate({
        id,
        requestBody: data
      })
      .then(() => this.fetchOne(id));
  }

  public async updateEmployeeGeneralData(userId: string, data: PatchedEmployeeGeneralDataRequest) {
    return await this.apiClient.employees
      .employeesGeneralDataPartialUpdate({ id: userId, requestBody: data })
      .then((response) => {
        this.partialStoreUpdate(userId, 'general_data', response);
      });
  }

  public async updateEmployeeAddressData(userId: string, data: PatchedEmployeeAddressRequest) {
    return await this.apiClient.employees
      .employeesAddressPartialUpdate({ id: userId, requestBody: data })
      .then((response) => {
        this.partialStoreUpdate(userId, 'address', response);
      });
  }

  public async updateEmployeeContactData(userId: string, data: PatchedEmployeeContactDataRequest) {
    return await this.apiClient.employees
      .employeesContactDataPartialUpdate({ id: userId, requestBody: data })
      .then((response) => {
        this.partialStoreUpdate(userId, 'contact_data', response);
      });
  }

  public async updateEmployeeTaxData(userId: string, data: PatchedEmployeeTaxDataRequest) {
    return await this.apiClient.employees
      .employeesTaxDataPartialUpdate({ id: userId, requestBody: data })
      .then((response) => {
        this.partialStoreUpdate(userId, 'tax_data', response);
      });
  }

  public async updateEmployeePositionData(
    userId: string,
    data: PatchedEmployeePositionDataRequest
  ) {
    return await this.apiClient.employees
      .employeesPositionDataPartialUpdate({ id: userId, requestBody: data })
      .then((response) => {
        this.partialStoreUpdate(userId, 'position_data', response);
      });
  }

  public async updateEmployeeGroupData(userId: string, values: EmployeeGroupUpdateRequest) {
    return await this.apiClient.employees
      .employeesUpdateGroupsUpdate({ id: userId, requestBody: values })
      .then((response) => {
        this.partialStoreUpdate(userId, 'groups', response.groups ?? []);
      });
  }

  public async bulkUpdateEmployeeGroupData(values: PatchedEmployeeBulkGroupRequest) {
    return await this.apiClient.employees.employeesBulkUpdateGroupsPartialUpdate({
      requestBody: values
    });
  }

  private performePartialStoreUpdate<
    T1 extends keyof EmployeeExtended,
    T2 extends EmployeeExtended[T1]
  >(employee: EmployeeExtended, part: T1, update: T2) {
    if (Array.isArray(employee[part])) {
      employee[part] = update;
    } else {
      employee[part] = Util.merge(employee[part], update);
    }
  }

  private partialStoreUpdate<T1 extends keyof EmployeeExtended, T2 extends EmployeeExtended[T1]>(
    userId: string,
    part: T1,
    update: T2
  ) {
    if (this._currentEmployee?.id === userId) {
      this.performePartialStoreUpdate(this._currentEmployee, part, update);
    }
  }

  private calcBenefitBudget(value: number, operation: OperationEnum, employee?: EmployeeExtended) {
    employee = employee ?? this._currentEmployee;
    return Util.calcBenefitBudget(operation === 'FIXED_VALUE' ? value / 100 : value, operation, {
      benefit_budget: employee?.benefit_budget.base_available_amount
    });
  }

  private assignBenefitBudget(newBudget: number, employee: EmployeeExtended) {
    const customBenefitsDelta =
      employee.benefit_budget.total_available_amount -
      employee.benefit_budget.base_available_amount;
    Util.assignReadOnly(employee.benefit_budget, 'base_available_amount', newBudget * 100);
    Util.assignReadOnly(
      employee.benefit_budget,
      'total_available_amount',
      newBudget * 100 + customBenefitsDelta
    );
  }

  private updateBenefitBudget(value: number, operation: OperationEnum, employee: EmployeeExtended) {
    const newBudget = this.calcBenefitBudget(value, operation, employee);
    this.assignBenefitBudget(operation === 'FIXED_VALUE' ? newBudget : newBudget / 100, employee);
  }

  public async updateBenefitBudgets(
    selectedUsers: Array<string>,
    value: number,
    operation: OperationEnum,
    startAccountingPeriodMonth: number,
    startAccountingPeriodYear: number
  ) {
    const res = await this.apiClient.employees.employeesUpdateBenefitBudgetsUpdate({
      company: this.companyStore.company?.id,
      requestBody: {
        query: {
          ids: selectedUsers
        },
        budget_update: {
          value,
          operation,
          start_accounting_period: {
            month: startAccountingPeriodMonth,
            year: startAccountingPeriodYear
          }
        }
      }
    });

    if ('rows_affected' in res && res.rows_affected > 0) {
      runInAction(() => {
        if (this._currentEmployee && selectedUsers.includes(this._currentEmployee?.id ?? '')) {
          this.updateBenefitBudget(value, operation, this._currentEmployee);
        }
      });
    }
  }

  public async bulkUpdateEmployees(
    selectedUsers: Array<string>,
    values: EmployeeBulkEditDataRequest
  ) {
    await this.apiClient.employees.employeesBulkEditUpdate({
      requestBody: {
        query: {
          ids: selectedUsers
        },
        data: values
      }
    });
  }

  public async updateEmployeePosition(employeeId: string, values: PatchedEmployeeExtendedRequest) {
    const res = await this.apiClient.employees.employeesPartialUpdate({
      id: employeeId,
      requestBody: values
    });

    if ('id' in res) {
      runInAction(() => {
        this._currentEmployee = res;
      });
    }
  }

  public async setNewPassword(
    employeeId: string,
    resetPasswordId: string,
    password: string,
    confirmPassword: string,
    initial?: boolean
  ) {
    return this.apiClient.employees.employeesSetNewPasswordCreate({
      id: employeeId,
      requestBody: {
        password_reset_id: resetPasswordId,
        password: password,
        confirm_password: confirmPassword,
        initial
      }
    });
  }

  public async changeUserPassword(request: ChangeUserPasswordRequest) {
    return this.apiClient.auth.authChangePasswordCreate({
      requestBody: request
    });
  }

  public async sendInvitationEmail(userId: string) {
    await this.apiClient.employees.employeesSendInvitationEmailCreate({ id: userId });
  }

  public async deleteEmployee(userId: string) {
    await this.apiClient.employees.employeesDestroy({ id: userId });
  }

  public async checkNewEmailAdress(email: string) {
    const response = await this.apiClient.user.userCheckEmailAddressCreate({
      requestBody: { email }
    });

    return response.available;
  }

  public async changeCurrentEmployee(id: string) {
    const employee = await this.fetchOne(id);
    runInAction(() => {
      this._currentEmployee = employee;
    });
  }
}

export default EmployeeStore;
